// Layer 3 (Transformation): §8.1 process graph for PlantModelScene, laid out
// faithfully to C1's page-12 worked-example figure ("BUNKER HILL CONCENTRATOR —
// PROCESS GRAPH"): five forward boxes left→right, tails thickener & paste
// offset below-right, the process-water system as a wide bottom box, internal
// recirculating loads drawn as semicircle arcs INSIDE their node with the
// annotation beneath, and the water network as light dashed arcs. Pure — no
// React, no engine. Topology comes from STREAMS; this file only places it.

import type { ScenarioPhase, SessionState } from '@/versions/v1/schema/scenario';
import type { WhatIfInputs } from '@/versions/v1/schema/scenario';
import { WHAT_IF_DEFAULTS } from '@/versions/v1/schema/scenario';
import { isWhatIfDefault } from '@/versions/v1/shared/ScenarioEngine';
export type { WhatIfInputs } from '@/versions/v1/schema/scenario';
export { WHAT_IF_DEFAULTS } from '@/versions/v1/schema/scenario';
import type {
  RecirculatingLoop,
  Stream,
  StreamSpec,
} from '@/versions/v1/schema/stream';
import type { SampleAdherence, SamplePoint, Subprocess, UnitOperation, UnitOpType } from '@/versions/v1/schema/unit-operation';
import { RECIRCULATING_LOOPS, STREAMS, STREAM_SPECS, SUBPROCESSES, UNIT_OPERATIONS } from '@/versions/v1/shared/bunker-hill-seed';
import {
  isInteriorStream,
  subprocessForNodeId,
} from '@/versions/v1/shared/bunker-hill-plant-nodes';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { scopeHealth } from '@/versions/v1/shared/reading.derive';
import { readingsAt } from '@/versions/v1/shared/series.derive';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import {
  plantStrip,
  stripNodeOrder,
  type PlantStripNode,
  type ResolvedUtilizationRank,
} from './Scene.transform';
import {
  isPeriodMeasured,
  MA_EDGE_LABEL_POS,
  metalAccountingLayer,
  periodWindows,
  suppressConstraintReadsOnNode,
  type PeriodView,
  type MaAnnotation,
  type MaClosureStripView,
  type NodeGapChip,
} from './MetalAccountingLayer.transform';
import type { AccountingPeriod } from '@/versions/v1/schema/metal-accounting';
import type { RollupLens } from '@/versions/v1/shared/rollup-lens';

export const GRAPH_VIEWBOX = { width: 1072, height: 490 } as const;

/** Figure grammar: forward = solid ink · water network = light dashed · loops = in-node accent arcs. */
type NodeBox = { x: number; y: number; w: number; h: number };

/** Top-row subprocess cards — base height; grows with interior sample chips. */
const SPINE_BOX: NodeBox = { x: 100, y: 84, w: 124, h: 120 };
const SPINE_BASE_H = 92;
const INTERIOR_CHIP_ROW_H = 40;
/** The plant-model route pins each interior chip to one 22 px line (plus the
 *  4 px column gap), so its cards size to that row instead of the wrapped one
 *  (polish.P11). The MA-active graph keeps the wrapped chip and its row. */
const INTERIOR_CHIP_ROW_H_ONE_LINE = 26;
const SPINE_NODE_IDS = ['sp-crush', 'sp-grind', 'sp-pb', 'sp-zn', 'sp-filter'] as const;
const SPINE_X: Record<(typeof SPINE_NODE_IDS)[number], number> = {
  'sp-crush': 100,
  'sp-grind': 280,
  'sp-pb': 460,
  'sp-zn': 640,
  'sp-filter': 820,
};

/** Card height from interior chip count (max two visible on the graph). */
export function spineNodeHeight(interiorChipCount: number, oneLineChips = false): number {
  if (interiorChipCount <= 0) return SPINE_BOX.h;
  const rowH = oneLineChips ? INTERIOR_CHIP_ROW_H_ONE_LINE : INTERIOR_CHIP_ROW_H;
  return Math.max(SPINE_BOX.h, SPINE_BASE_H + Math.min(interiorChipCount, 2) * rowH + 10);
}

const FIXED_NODE_BOXES: Record<string, NodeBox> = {
  'sp-paste': { x: 624, y: 268, w: 150, h: 88 },
  'sp-water': { x: 200, y: 384, w: 430, h: 76 },
  'external-in': { x: 6, y: 120, w: 82, h: 48 },
  // The product column stands 32 units clear of Thicken, so its two edges
  // read as arrows rather than bare arrowheads (polish.P11 r1).
  'external-cake': { x: 976, y: 58, w: 122, h: 56 },
  'external-zncake': { x: 976, y: 140, w: 106, h: 56 },
  'external-paste': { x: 976, y: 284, w: 106, h: 56 },
  'external-makeup': { x: 40, y: 398, w: 104, h: 48 },
};

const SPINE_MID_Y = SPINE_BOX.y + SPINE_BOX.h / 2;
const SPINE_EDGE_LABEL_Y = SPINE_MID_Y + 16;
const SPINE_CHIP_Y = SPINE_BOX.y + 24;
const LOOP_LABEL_GAP = 10;
const DEFAULT_SPINE_BOTTOM = SPINE_BOX.y + SPINE_BOX.h;
const PASTE_BELOW_SPINE = FIXED_NODE_BOXES['sp-paste'].y - DEFAULT_SPINE_BOTTOM;
const WATER_BELOW_SPINE = FIXED_NODE_BOXES['sp-water'].y - DEFAULT_SPINE_BOTTOM;

type GraphLayout = {
  nodeBoxes: Record<string, NodeBox>;
  maxSpineBottom: number;
};

/** Every spine box height follows {@link spineNodeHeight}; loop-label band tracks the tallest card. */
export function buildGraphLayout(
  streams: readonly Stream[],
  samplePoints: readonly SamplePoint[],
  oneLineChips = false,
  /** False under the GM lens, whose cards drop their interior chips, so no
   *  card keeps the height of chips it does not draw. */
  interiorChips = true,
): GraphLayout {
  const nodeBoxes: Record<string, NodeBox> = { ...FIXED_NODE_BOXES };
  for (const id of SPINE_NODE_IDS) {
    const chipCount = interiorChips ? interiorEdgeChipsForNode(id, streams, samplePoints).visible.length : 0;
    nodeBoxes[id] = {
      x: SPINE_X[id],
      y: SPINE_BOX.y,
      w: SPINE_BOX.w,
      h: spineNodeHeight(chipCount, oneLineChips),
    };
  }
  const maxSpineBottom = Math.max(
    ...SPINE_NODE_IDS.map((id) => nodeBoxes[id]!.y + nodeBoxes[id]!.h),
  );
  nodeBoxes['sp-paste'] = {
    ...nodeBoxes['sp-paste']!,
    y: maxSpineBottom + PASTE_BELOW_SPINE,
  };
  nodeBoxes['sp-water'] = {
    ...nodeBoxes['sp-water']!,
    y: maxSpineBottom + WATER_BELOW_SPINE,
  };
  // The paste and make-up stubs ride with the card they feed, so their edges
  // stay level when interior chips push the lower row down (polish.P11 r1).
  const centreOn = (stubId: string, cardId: string) => {
    const stub = nodeBoxes[stubId]!;
    const card = nodeBoxes[cardId]!;
    nodeBoxes[stubId] = { ...stub, y: card.y + (card.h - stub.h) / 2 };
  };
  centreOn('external-paste', 'sp-paste');
  centreOn('external-makeup', 'sp-water');
  return { nodeBoxes, maxSpineBottom };
}

export type ExternalStubView = {
  id: string;
  label: string;
  subLines: readonly string[];
  x: number;
  y: number;
  w: number;
  h: number;
  /** MA-locked: the narrow stub column wraps its label and value lines at
   *  10 px instead of truncating them. */
  wrap?: boolean;
};

/** Right-side output labels — product masses and grades from `periodView`
 *  when measured; what-if is ignored whenever a balance exists (R2/R3).
 *  `periodOnly` (the MA-locked path) drops the design-constant tails the MA
 *  layer has no period read for — the same rule the loop and edge labels
 *  take: a period read where one exists, otherwise omitted (r11 SHOULD-FIX 3). */
function externalStubs(
  nodeBoxes: Record<string, NodeBox>,
  whatIf?: WhatIfDerived | null,
  view?: PeriodView,
  periodOnly = false,
): ExternalStubView[] {
  const resolved = view ?? periodWindows().day;
  const measured = isPeriodMeasured(resolved);
  const unmeasured = 'unmeasured';
  const shiftUnmeasured = resolved.period === 'shift';
  // One locale formatter across the stubs: `1,100.5 dst` on the stub and on the
  // Mass balance table eight rows below it, never `1100.5` beside `1,100.5`
  // (r11 SHOULD-FIX 2).
  const dst1 = (value: number) =>
    value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const pbFlow = measured
    ? `${dst1(resolved.masses.C1)} dst`
    : shiftUnmeasured
      ? unmeasured
      : whatIf
        ? `${dst1(56.9 * whatIf.scale * (whatIf.pbRecPct / 84))} dst`
        : unmeasured;
  const znFlow = measured
    ? `${dst1(resolved.masses.C2)} dst`
    : shiftUnmeasured
      ? unmeasured
      : whatIf
        ? `${dst1(143.7 * whatIf.scale * (whatIf.znRecPct / 87))} dst`
        : unmeasured;
  const romFeed = measured
    ? resolved.period === 'day'
      ? `${Math.round(resolved.feed).toLocaleString('en-US')} dst/d`
      : `${Math.round(resolved.feed).toLocaleString('en-US')} dst`
    : shiftUnmeasured
      ? unmeasured
      : whatIf
        ? `${fmtInt((1800 * whatIf.scale) / 1)} dst/d`
        : unmeasured;
  const pbGrade = measured ? `${resolved.productGrade.pbConcPct.toFixed(1)}%` : '';
  const znGrade = measured ? `${resolved.productGrade.znConcPct.toFixed(1)}%` : '';
  // The tail two stubs carry a design constant, not a period read — the same
  // class of figure the loop and edge labels dropped on the MA-locked route
  // (r11 SHOULD-FIX 3). The stub stays (it is a flowsheet node the drawn edges
  // land on); only the constant goes.
  const pasteSub = periodOnly ? [] : ['350 dst/d target'];
  const makeupSub = periodOnly ? [] : ['~5 gpm'];
  const rows: { id: string; label: string; subLines: string[] }[] = [
    { id: 'external-in', label: 'ROM feed', subLines: [romFeed] },
    {
      id: 'external-cake',
      label: 'Pb/Ag concentrate',
      subLines: measured ? [pbFlow, pbGrade] : [pbFlow],
    },
    {
      id: 'external-zncake',
      label: 'Zn concentrate',
      subLines: measured ? [znFlow, znGrade] : [znFlow],
    },
    { id: 'external-paste', label: 'Paste backfill', subLines: pasteSub },
    { id: 'external-makeup', label: 'Make-up water', subLines: makeupSub },
  ];
  return rows.map((row) => ({ ...row, ...nodeBoxes[row.id] }));
}

function anchor(
  nodeBoxes: Record<string, NodeBox>,
  id: string,
  side: 'left' | 'right' | 'top' | 'bottom',
  along = 0.5,
): { x: number; y: number } {
  const b = nodeBoxes[id];
  if (!b) return { x: 0, y: 0 };
  switch (side) {
    case 'left':
      return { x: b.x, y: b.y + b.h * along };
    case 'right':
      return { x: b.x + b.w, y: b.y + b.h * along };
    case 'top':
      return { x: b.x + b.w * along, y: b.y };
    case 'bottom':
      return { x: b.x + b.w * along, y: b.y + b.h };
  }
}

/**
 * Every rendered edge is placed explicitly — no generic router. Coordinates
 * chosen so no path crosses a node box or a loop annotation (checked against
 * NODE_BOXES and LOOP_LABEL boxes; keep it that way when editing).
 */
type EdgeDecor = {
  path: string;
  /** Small value label, figure-style. */
  label?: { text: string; x: number; y: number; anchor?: 'start' | 'middle' | 'end' };
  /** Sample chip anchor: its centre, or its left edge with `anchor: 'start'`
   *  for a chip beside its edge that must grow away from it. */
  chip?: { x: number; y: number; anchor?: 'middle' | 'start' };
};

function line(a: { x: number; y: number }, b: { x: number; y: number }): string {
  return `M ${a.x} ${a.y} L ${b.x} ${b.y}`;
}

type PathPoint = { x: number; y: number };

function cubicAt(p0: PathPoint, p1: PathPoint, p2: PathPoint, p3: PathPoint, t: number): PathPoint {
  const u = 1 - t;
  const uu = u * u;
  const tt = t * t;
  const uuu = uu * u;
  const ttt = tt * t;
  return {
    x: uuu * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + ttt * p3.x,
    y: uuu * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + ttt * p3.y,
  };
}

function dist(a: PathPoint, b: PathPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** Midpoint (t = 0.5) along an SVG path string used by the graph edges. */
export function samplePointOnPath(path: string, t = 0.5): PathPoint {
  const tokens = path.trim().match(/[MLCZ]|-?[\d.]+/g);
  if (!tokens?.length) return { x: 0, y: 0 };

  const segments: { kind: 'line' | 'cubic'; points: PathPoint[]; length: number }[] = [];
  let i = 0;
  let cmd = '';
  let cursor: PathPoint = { x: 0, y: 0 };

  const readNum = () => Number.parseFloat(tokens[i++] ?? '0');

  while (i < tokens.length) {
    const token = tokens[i];
    if (token === 'M' || token === 'L' || token === 'C' || token === 'Z') {
      cmd = token;
      i += 1;
      continue;
    }
    if (cmd === 'M') {
      cursor = { x: readNum(), y: readNum() };
      cmd = 'L';
      continue;
    }
    if (cmd === 'L') {
      const end = { x: readNum(), y: readNum() };
      segments.push({ kind: 'line', points: [cursor, end], length: dist(cursor, end) });
      cursor = end;
      continue;
    }
    if (cmd === 'C') {
      const p1 = { x: readNum(), y: readNum() };
      const p2 = { x: readNum(), y: readNum() };
      const p3 = { x: readNum(), y: readNum() };
      const chord = dist(cursor, p3);
      segments.push({ kind: 'cubic', points: [cursor, p1, p2, p3], length: chord });
      cursor = p3;
      continue;
    }
    i += 1;
  }

  const total = segments.reduce((sum, seg) => sum + seg.length, 0);
  if (total === 0) return cursor;
  const target = total * t;
  let walked = 0;
  for (const seg of segments) {
    if (walked + seg.length >= target || seg === segments[segments.length - 1]) {
      const localT = seg.length === 0 ? 0 : (target - walked) / seg.length;
      if (seg.kind === 'line') {
        const [a, b] = seg.points;
        return { x: a.x + (b.x - a.x) * localT, y: a.y + (b.y - a.y) * localT };
      }
      const [p0, p1, p2, p3] = seg.points;
      return cubicAt(p0, p1, p2, p3, Math.min(1, Math.max(0, localT)));
    }
    walked += seg.length;
  }
  return cursor;
}

function offsetChipFromDot(dot: PathPoint, chip: PathPoint, minGap = 10): PathPoint {
  const gap = dist(dot, chip);
  if (gap >= minGap) return chip;
  return { x: chip.x, y: chip.y - (minGap - gap) };
}

/** A stream's seeded nominal figure without its gloss: `1,584 dst (88.0% of
 *  feed mass)` → `1,584 dst`. Empty when the stream is not seeded. */
export function streamNominalFigure(streams: readonly Stream[], streamId: string): string {
  const nominal = streams.find((stream) => stream.id === streamId)?.nominalFlow ?? '';
  return nominal.replace(/\s*\(.*\)\s*$/, '');
}

function edgeDecor(nodeBoxes: Record<string, NodeBox>, streams: readonly Stream[]): Record<string, EdgeDecor> {
  const paste = nodeBoxes['sp-paste']!;
  const water = nodeBoxes['sp-water']!;
  const crush = nodeBoxes['sp-crush']!;
  const grind = nodeBoxes['sp-grind']!;
  const pb = nodeBoxes['sp-pb']!;
  const zn = nodeBoxes['sp-zn']!;
  const filter = nodeBoxes['sp-filter']!;
  const grindBottom = grind.y + grind.h;
  const pbBottom = pb.y + pb.h;
  const znBottom = zn.y + zn.h;
  const filterBottom = filter.y + filter.h;
  const waterTop = water.y;
  const waterMidY = water.y + water.h / 2;
  const pasteBottom = paste.y + paste.h;
  const millX = grind.x + 24;
  const pbWaterX = pb.x + 20;
  const znWaterX = zn.x + 12;
  const filterDropX = filter.x + 46;
  const znDropX = zn.x + 100;
  const pasteInX = paste.x + 88;
  const pasteOfX = paste.x + 75;
  const a = (id: string, side: 'left' | 'right' | 'top' | 'bottom', along = 0.5) =>
    anchor(nodeBoxes, id, side, along);
  // A spine link runs level: both cards share a top, so the shallower card's
  // mid-height sits inside both. Anchoring each end at its own card's middle
  // tilted the arrow whenever sample chips made one card taller, and the
  // tilted arrow ran through its flow label (polish.P11).
  const spine = (from: string, to: string) => {
    const f = nodeBoxes[from];
    const t = nodeBoxes[to];
    if (!f || !t) return line(a(from, 'right'), a(to, 'left'));
    const y = Math.min(f.y + f.h / 2, t.y + t.h / 2);
    return line({ x: f.x + f.w, y }, { x: t.x, y });
  };
  // A stub edge runs level through the band both boxes share, so a product
  // stub beside a taller card is not reached by a slanted stub of a line.
  const level = (from: string, to: string, fallback: string) => {
    const f = nodeBoxes[from];
    const t = nodeBoxes[to];
    if (!f || !t) return fallback;
    const top = Math.max(f.y, t.y);
    const bottom = Math.min(f.y + f.h, t.y + t.h);
    if (bottom <= top) return fallback;
    const y = (top + bottom) / 2;
    return line({ x: f.x + f.w, y }, { x: t.x, y });
  };
  // Spine labels and chips sit on their corridor's midpoint.
  const corridor = (from: NodeBox, to: NodeBox) => mid(right(from), to.x);
  return {
    'st-rom': { path: level('external-in', 'sp-crush', line(a('external-in', 'right'), a('sp-crush', 'left'))) },
    'st-crushed': {
      path: spine('sp-crush', 'sp-grind'),
      label: { text: '75 stph', x: corridor(crush, grind), y: SPINE_EDGE_LABEL_Y, anchor: 'middle' },
    },
    'st-cyc-of': {
      path: spine('sp-grind', 'sp-pb'),
      label: { text: '1,880 dst', x: corridor(grind, pb), y: SPINE_EDGE_LABEL_Y, anchor: 'middle' },
      chip: { x: corridor(grind, pb), y: SPINE_CHIP_Y },
    },
    'st-pbtail': {
      path: spine('sp-pb', 'sp-zn'),
      label: { text: '1,728 dst', x: corridor(pb, zn), y: SPINE_EDGE_LABEL_Y, anchor: 'middle' },
      chip: { x: corridor(pb, zn), y: SPINE_CHIP_Y },
    },
    'st-zncon': {
      path: spine('sp-zn', 'sp-filter'),
      label: { text: '143.7 dst', x: corridor(zn, filter), y: SPINE_EDGE_LABEL_Y, anchor: 'middle' },
      chip: { x: corridor(zn, filter), y: SPINE_CHIP_Y },
    },
    // Pb concentrate to the filter — not drawn in the figure (its top row is
    // the forward spine); rendered as a thin over-arc so the seed edge and
    // SA03 keep a home without disturbing the figure's row.
    // Takes off right-of-centre so the Pb loop arc owns the node-top centre.
    'st-pbcon': {
      path: `M ${pb.x + 104} ${SPINE_BOX.y} C ${pb.x + 104} 46, 604 30, 712 30 C 850 30, ${filter.x + 62} 44, ${filter.x + 62} ${SPINE_BOX.y}`,
      chip: { x: 712, y: 30 },
    },
    'st-tails': {
      path: `M ${znDropX} ${znBottom} C ${znDropX + 14} ${znBottom + 28}, ${znDropX + 4} ${paste.y - 20}, ${pasteInX} ${paste.y}`,
      // Left of the arrowhead, clear of the SA05 chip and its word on the right.
      label: { text: streamNominalFigure(streams, 'st-tails'), x: pasteInX - 12, y: paste.y - 8, anchor: 'end' },
      chip: { x: 800, y: paste.y - 6 },
    },
    'st-paste': {
      path: level('sp-paste', 'external-paste', line(a('sp-paste', 'right'), a('external-paste', 'left'))),
    },
    'st-pbcake': {
      path: level('sp-filter', 'external-cake', line(a('sp-filter', 'right', 0.25), a('external-cake', 'left', 0.55))),
    },
    'st-zncake': {
      path: level('sp-filter', 'external-zncake', line(a('sp-filter', 'right', 0.7), a('external-zncake', 'left'))),
    },
    'st-water-mill': {
      path: `M ${millX - 4} ${waterTop} C ${millX - 4} ${waterTop - 84}, ${millX} ${grindBottom + 64}, ${millX} ${grindBottom}`,
      label: { text: '~300 gpm', x: millX - 12, y: (waterTop + grindBottom) / 2, anchor: 'end' },
    },
    'st-water-pb': {
      path: `M 420 ${waterTop} C 420 ${waterTop - 84}, ${pbWaterX} ${pbBottom + 74}, ${pbWaterX} ${pbBottom}`,
    },
    'st-water-zn': {
      path: `M 560 ${waterTop} C 560 ${waterTop - 84}, ${znWaterX} ${znBottom + 80}, ${znWaterX} ${znBottom}`,
    },
    'st-tails-of': {
      path: `M ${pasteOfX} ${pasteBottom} C ${pasteOfX} ${(pasteBottom + waterTop) / 2}, 650 ${waterTop}, 600 ${waterTop}`,
      label: { text: '451 gpm', x: pasteOfX + 13, y: (pasteBottom + waterTop) / 2, anchor: 'start' },
    },
    'st-conc-of': {
      path: `M ${filterDropX} ${filterBottom} C ${filterDropX} ${waterTop - 84}, 780 ${waterMidY}, 630 ${waterMidY}`,
    },
    'st-makeup': {
      path: level('external-makeup', 'sp-water', line(a('external-makeup', 'right'), a('sp-water', 'left'))),
    },
  };
}

/** The three internal loops render inside their node (figure grammar). */
const SELF_LOOP_STREAMS = new Set(['st-cyc-uf', 'st-pb-cln-tail', 'st-zn-cln-tail']);

export type GraphLoopLabel = {
  nodeId: string;
  title: string;
  value: string;
  x: number;
  y: number;
  w: number;
};

/** Annotation boxes under the loop nodes — y tracks tallest spine card (see buildGraphLayout). */
const LOOP_LABEL_X: Record<string, { x: number; w: number }> = {
  'sp-grind': { x: 332, w: 142 },
  'sp-pb': { x: 512, w: 96 },
  'sp-zn': { x: 664, w: 80 },
};

function loopLabels(
  loops: readonly RecirculatingLoop[],
  streams: readonly Stream[],
  maxSpineBottom: number,
  whatIf?: WhatIfDerived | null,
  periodOnly = false,
): GraphLoopLabel[] {
  const titleByNode: Record<string, string> = {
    'sp-grind': 'Cyclone U/F recycle',
    'sp-pb': 'Pb cleaner tail',
    'sp-zn': 'Zn cleaner tail',
  };
  const whatIfValueByNode: Record<string, string> = whatIf
    ? {
        'sp-grind': `${whatIf.circUfStph} stph · ${whatIf.circPct}% of new feed`,
        'sp-pb': `${(79.9 * whatIf.scale).toFixed(1)} dst`,
        'sp-zn': `${(67.8 * whatIf.scale).toFixed(1)} dst`,
      }
    : {};
  return streams
    .filter((s) => SELF_LOOP_STREAMS.has(s.id))
    .map((s) => {
      const loop = loops.find((row) => row.streamId === s.id);
      const box = LOOP_LABEL_X[s.from];
      const flow = s.nominalFlow.trim();
      const ratio = loop?.loadRatio?.trim() ?? '';
      // `periodOnly` (the MA-locked path) has no period-derived recirculating
      // load to show, so the value is omitted rather than filled with the
      // plant-model nominal (r10 MUST-FIX 4).
      return {
        nodeId: s.from,
        title: titleByNode[s.from] ?? s.name,
        value: periodOnly ? '' : whatIfValueByNode[s.from] ?? [flow, ratio].filter(Boolean).join(' · '),
        x: box?.x ?? 0,
        y: maxSpineBottom + LOOP_LABEL_GAP,
        w: box?.w ?? 100,
      };
    });
}

export type FlowDotView = {
  id: string;
  offset: number;
  radius: number;
  durationSec: number;
  beginSec: number;
};

export type EdgeSampleChip = {
  id: string;
  adherence: SampleAdherence;
  tone: VizTone;
  label: string;
  x: number;
  y: number;
  /** How the chip hangs off (x, y): centred, or left-aligned to x. */
  anchor: 'middle' | 'start';
  /** Sample dot on the edge path (distinct from the chip label anchor). */
  dotX: number;
  dotY: number;
};

export type CircuitKind = 'pb' | 'zn' | 'none';

export type GraphEdgeView = {
  stream: Stream;
  path: string;
  strokeWidth: number;
  dashed: boolean;
  opacity: number;
  tooltipTitle: string;
  valueLabel: { text: string; x: number; y: number; anchor: 'start' | 'middle' | 'end' } | null;
  sampleChips: readonly EdgeSampleChip[];
  dots: readonly FlowDotView[];
  dotColor: string;
  circuit: CircuitKind;
  /** Metal-accounting layer lines on this edge (empty when the layer is off). */
  maLabels: readonly MaEdgeLabel[];
};

export type MaEdgeLabel = MaAnnotation & {
  x: number;
  y: number;
  anchor: 'start' | 'middle' | 'end';
};

/**
 * MA edge labels are anchored by `MA_EDGE_LABEL_POS` in
 * `MetalAccountingLayer.transform` — imported, never copied, so the corridor
 * geometry has one owner (r11 SHOULD-FIX 5).
 */

export type InteriorEdgeRow = {
  streamId: string;
  /** Chip label — e.g. "SA02 · rougher feed". */
  chipLabel: string;
  /** The plant-model route's one-line chip text, as its comp prints it:
   *  "SA02 rougher feed" on a routine stream; the sample id alone on an
   *  off-routine one, whose adherence word follows it. */
  plantChipText: string;
  sampleIds: readonly string[];
  adherence: SampleAdherence;
  tone: VizTone;
};

const INTERIOR_CHIP_VISIBLE = 2;

function interiorChipLabel(
  sampleIds: readonly string[],
  samplePoints: readonly SamplePoint[],
  streamName: string,
): string {
  const id = sampleIds[0];
  if (!id) return streamName;
  const point = samplePoints.find((p) => p.id === id);
  return `${id} · ${interiorChipStream(point, streamName)}`;
}

function interiorChipStream(point: SamplePoint | undefined, streamName: string): string {
  return point?.stream.replace(/^Pb rougher /, 'rougher ').replace(/^Pb /, '') ?? streamName;
}

function plantChipText(
  sampleIds: readonly string[],
  samplePoints: readonly SamplePoint[],
  streamName: string,
  adherence: SampleAdherence,
): string {
  const id = sampleIds[0];
  if (!id) return streamName;
  if (adherence !== 'REG') return id;
  return `${id} ${interiorChipStream(samplePoints.find((p) => p.id === id), streamName)}`;
}

/** Interior streams with sample points inside this subprocess area. */
export function interiorEdgesForNode(
  nodeId: string,
  streams: readonly Stream[],
  samplePoints: readonly SamplePoint[],
): InteriorEdgeRow[] {
  return streams
    .filter((edge) => {
      if (edge.samplePointIds.length === 0) return false;
      if (!isInteriorStream(edge.from, edge.to)) return false;
      return subprocessForNodeId(edge.from) === nodeId;
    })
    .map((edge) => {
      const sampleIds = edge.samplePointIds;
      const first = samplePoints.find((p) => sampleIds.includes(p.id));
      const adherence = first?.adherence ?? 'REG';
      return {
        streamId: edge.id,
        chipLabel: interiorChipLabel(sampleIds, samplePoints, edge.name),
        plantChipText: plantChipText(sampleIds, samplePoints, edge.name, adherence),
        sampleIds,
        adherence,
        tone: adherenceTone(adherence),
      };
    });
}

/** Visible interior chips on a node card (max two; remainder on Streams tab). */
export function interiorEdgeChipsForNode(
  nodeId: string,
  streams: readonly Stream[],
  samplePoints: readonly SamplePoint[],
): { visible: InteriorEdgeRow[]; overflow: number } {
  const rows = interiorEdgesForNode(nodeId, streams, samplePoints);
  return {
    visible: rows.slice(0, INTERIOR_CHIP_VISIBLE),
    overflow: Math.max(0, rows.length - INTERIOR_CHIP_VISIBLE),
  };
}

export type GraphNodeView = PlantStripNode & NodeBox & {
  topToneClass: string;
  barClass: string;
  bottleneckLabel: string | null;
  wide: boolean;
  interiorEdges: InteriorEdgeRow[];
  interiorOverflow: number;
  /** Primary unit-op glyph for this subprocess; null for utility / external cells. */
  unitOpType: UnitOpType | null;
  /** Metal-accounting layer sub-line (renders in place of `sub`; null when the layer is off). */
  maNote: MaAnnotation | null;
  /** Per-stage closure gap for the measured period, on the MA-locked route
   *  only. Rides the title row, so it costs the card no height (r11
   *  SHOULD-FIX 1: the chip was nested inside the day-only `maNote` branch and
   *  the WTD / MTD gaps — the one warn-toned read on the route — never
   *  rendered). */
  maGap: NodeGapChip | null;
  /** The card is drawn by the locked metal-accounting layout
   *  ({@link maLockedLayout}): no unit-op glyph, and each interior sample chip
   *  is a full-width two-line row, so `SA06 · rougher concentrate` reads whole
   *  instead of truncating. False everywhere else. */
  maCompact: boolean;
};

// ---------------------------------------------------------------------------
// The locked metal-accounting layout (polish.P9 r2, #657).
//
// `/metal-accounting` draws the graph in the 8-column flowsheet panel beside
// the stage panel, as the locked comp `metal-accounting.html` A does. The
// plant-model layout is 1,072 units wide, so `fitView` shrank it to ~0.66 and
// node text rendered at 6.6 px, under the 10 px floor. The locked route
// therefore takes its own compact layout, sized to fit that pane at zoom 1.
// Every position below is derived from the column widths and the card
// heights — corridor midpoints for the sample chips and the MA edge labels,
// box edges for every path — so a width change moves its marks with it. The
// plant-model layout above is untouched.
// ---------------------------------------------------------------------------

/** Left to right: the ROM stub, the five spine cards, the product-stub column. */
const MA_LOCKED_COLUMNS = [
  { id: 'external-in', w: 68 },
  { id: 'sp-crush', w: 72 },
  { id: 'sp-grind', w: 88 },
  { id: 'sp-pb', w: 126 },
  { id: 'sp-zn', w: 110 },
  { id: 'sp-filter', w: 92 },
  { id: 'stubs', w: 72 },
] as const;
/** The corridor after each column but the last — room for the arrow, with the
 *  sample chip straddling it as the comp draws it. */
const MA_LOCKED_CORRIDORS = [12, 20, 26, 26, 26, 14] as const;

/** Crest of the Pb-concentrate over-arc, where SA03 sits. */
const MA_LOCKED_ARC_Y = 24;
/** Top of the spine row: the arc rises from the card tops to its crest. */
const MA_LOCKED_SPINE_Y = 58;
/** A card with a period read: the 20 px title row plus a figure line and up
 *  to three 10 px sub-lines, inside the card's 8 px padding. */
const MA_LOCKED_BODY_H = 96;
/** A card with its title only. */
const MA_LOCKED_TITLE_H = 40;
/** One interior sample-chip row: a two-line chip (31 px, pinned in
 *  `ProcessGraph.tsx`) plus the 4 px column gap. The chip and this constant
 *  are a pair. */
const MA_LOCKED_CHIP_ROW_H = 35;
/** The two MA lines under the Zn-concentrate corridor: first line below the
 *  card bottoms, line pitch, half a line (the anchor is the line's centre). */
const MA_LOCKED_LABEL_OFFSET = 12;
const MA_LOCKED_LABEL_PITCH = 14;
const MA_LOCKED_HALF_LINE = 7;
/** Label band → paste (room for SA05 and its adherence flag on the tails
 *  edge), and the paste row → water. */
const MA_LOCKED_PASTE_GAP = 40;
const MA_LOCKED_WATER_GAP = 36;
const MA_LOCKED_PASTE_W = 112;
/** Sample chip above its forward edge (chip centre to the edge line). */
const MA_LOCKED_CHIP_RISE = 18;
/** The graph's top: the SA03 chip on the arc crest and the Pb-concentrate
 *  label beside it both clear it. */
const MA_LOCKED_TOP = 0;

/** Product stubs: fixed heights for their wrapped label + value lines. */
const MA_LOCKED_STUB_H: Record<string, number> = {
  'external-in': 48,
  'external-cake': 58,
  'external-zncake': 58,
  'external-paste': 36,
  'external-makeup': 28,
};
const MA_LOCKED_STUB_GAP = 8;

/** Whether an MA-locked card renders more than its short name — period reads only.
 *  Interior sample chips carry lab-coverage state, not period figures, so they
 *  do not by themselves give a card a body (r10 MUST-FIX 2). Neither does the
 *  closure-gap chip: it sits on the title row, which the title-only height
 *  already carries (r11 SHOULD-FIX 1). */
export function maLockedNodeShowsBody(node: GraphNodeView): boolean {
  if (node.maNote) return true;
  if (node.headline) return true;
  return !!node.sub;
}

/** Card height from what an MA-locked card renders: a body or the title
 *  alone, plus one pinned row per interior chip. Presentation heights only,
 *  never a guess at how a label wraps. */
export function maLockedNodeHeight(node: GraphNodeView): number {
  const base = maLockedNodeShowsBody(node) ? MA_LOCKED_BODY_H : MA_LOCKED_TITLE_H;
  return base + Math.min(node.interiorEdges.length, INTERIOR_CHIP_VISIBLE) * MA_LOCKED_CHIP_ROW_H;
}

type MaEdgeLabelPos = { x: number; y: number; anchor: 'start' | 'middle' | 'end' };

export type MaLockedLayout = {
  nodes: GraphNodeView[];
  boxes: Record<string, NodeBox>;
  decor: Record<string, EdgeDecor>;
  /** MA edge-label anchors for this layout (the plant-model overlay keeps
   *  `MA_EDGE_LABEL_POS`, placed for the plant-model boxes). */
  labelPos: Record<string, readonly MaEdgeLabelPos[]>;
  /** Top and bottom of everything the graph draws, before any stretch. */
  top: number;
  naturalBottom: number;
};

const mid = (a: number, b: number) => Math.round((a + b) / 2);
const right = (box: NodeBox) => box.x + box.w;
const bottom = (box: NodeBox) => box.y + box.h;

/** Lay the locked graph out: columns left to right, the spine row, the MA
 *  label band, paste, water and the stubs. `fillHeight` (graph units) is the
 *  height the host pane offers: when it exceeds the natural height, the extra
 *  goes to the two gaps under the spine (label band → paste, paste → water),
 *  so the graph fills its panel instead of floating in it. */
export function maLockedLayout(
  nodes: readonly GraphNodeView[],
  fillHeight?: number,
): MaLockedLayout {
  const xs: Record<string, { x: number; w: number }> = {};
  let x = 0;
  MA_LOCKED_COLUMNS.forEach((column, index) => {
    xs[column.id] = { x, w: column.w };
    x += column.w + (MA_LOCKED_CORRIDORS[index] ?? 0);
  });

  const sized = nodes.map((node) =>
    node.id.startsWith('sp-') ? { ...node, h: maLockedNodeHeight(node), maCompact: true } : node,
  );
  const byId = (id: string) => sized.find((node) => node.id === id);

  const spine: Record<string, NodeBox> = {};
  for (const id of SPINE_NODE_IDS) {
    const node = byId(id);
    spine[id] = { ...xs[id]!, y: MA_LOCKED_SPINE_Y, h: node?.h ?? MA_LOCKED_TITLE_H };
  }
  const [crush, grind, pb, zn, filter] = SPINE_NODE_IDS.map((id) => spine[id]!);
  const spineBottom = Math.max(...SPINE_NODE_IDS.map((id) => bottom(spine[id]!)));
  // Forward edges run level across the row, through the shortest card's middle.
  const rowMidY = MA_LOCKED_SPINE_Y + Math.min(...SPINE_NODE_IDS.map((id) => spine[id]!.h)) / 2;

  // The Zn-concentrate MA lines sit under their corridor, below both cards.
  const znCorridorX = mid(right(zn), filter.x);
  const labelY = Math.max(bottom(zn), bottom(filter)) + MA_LOCKED_LABEL_OFFSET;
  const labelBandBottom = labelY + MA_LOCKED_LABEL_PITCH + MA_LOCKED_HALF_LINE;
  const crestX = mid(zn.x, right(zn));

  const pasteNode = byId('sp-paste');
  const pasteH = pasteNode && maLockedNodeShowsBody(pasteNode) ? MA_LOCKED_BODY_H : MA_LOCKED_TITLE_H;
  const waterH = MA_LOCKED_TITLE_H;
  const naturalPasteY = labelBandBottom + MA_LOCKED_PASTE_GAP;
  const naturalWaterY = Math.max(spineBottom, naturalPasteY + pasteH) + MA_LOCKED_WATER_GAP;
  const naturalBottom = naturalWaterY + waterH;
  const extra = fillHeight && fillHeight > naturalBottom - MA_LOCKED_TOP
    ? Math.floor(fillHeight - (naturalBottom - MA_LOCKED_TOP))
    : 0;
  const pasteY = naturalPasteY + Math.floor(extra / 2);
  const paste: NodeBox = { x: zn.x + Math.round(zn.w * 0.4), y: pasteY, w: MA_LOCKED_PASTE_W, h: pasteH };
  const pasteInX = paste.x + 24;
  const waterY = Math.max(spineBottom, bottom(paste)) + MA_LOCKED_WATER_GAP + (extra - Math.floor(extra / 2));
  const water: NodeBox = { x: grind.x, y: waterY, w: mid(paste.x, right(paste)) + 24 - grind.x, h: waterH };

  const stubX = xs.stubs!;
  const stub = (id: string, y: number, box: { x: number; w: number } = stubX): NodeBox => ({
    ...box,
    y: Math.round(y),
    h: MA_LOCKED_STUB_H[id]!,
  });
  const cake = stub('external-cake', MA_LOCKED_SPINE_Y - 30);
  const zncake = stub('external-zncake', bottom(cake) + MA_LOCKED_STUB_GAP);
  const boxes: Record<string, NodeBox> = {
    ...spine,
    'sp-paste': paste,
    'sp-water': water,
    'external-in': stub('external-in', rowMidY - MA_LOCKED_STUB_H['external-in']! / 2, xs['external-in']),
    'external-cake': cake,
    'external-zncake': zncake,
    'external-paste': stub(
      'external-paste',
      Math.max(paste.y + (paste.h - MA_LOCKED_STUB_H['external-paste']!) / 2, bottom(zncake) + MA_LOCKED_STUB_GAP),
    ),
    'external-makeup': stub(
      'external-makeup',
      water.y + (water.h - MA_LOCKED_STUB_H['external-makeup']!) / 2,
      { x: 0, w: grind.x - 16 },
    ),
  };

  const placed = sized.map((node) => {
    const box = boxes[node.id];
    return box ? { ...node, ...box } : node;
  });

  const at = (box: NodeBox, side: 'left' | 'right', y: number) => ({ x: side === 'left' ? box.x : right(box), y });
  const a = (id: string, side: 'left' | 'right' | 'top' | 'bottom', along = 0.5) => anchor(boxes, id, side, along);
  const corridorChip = (from: NodeBox, to: NodeBox) => ({ x: mid(right(from), to.x), y: rowMidY - MA_LOCKED_CHIP_RISE });
  const rom = boxes['external-in']!;
  const pbconFrom = pb.x + Math.round(pb.w * 0.78);
  const pbconTo = filter.x + Math.round(filter.w * 0.4);
  const tailsFromX = crestX;
  const znWaterX = zn.x + 6;
  const tailsOfX = paste.x + 30;
  const filterDropX = mid(filter.x, right(filter));
  const waterMidY = water.y + water.h / 2;
  const decor: Record<string, EdgeDecor> = {
    'st-rom': { path: line(at(rom, 'right', rowMidY), at(crush, 'left', rowMidY)) },
    'st-crushed': { path: line(at(crush, 'right', rowMidY), at(grind, 'left', rowMidY)) },
    'st-cyc-of': { path: line(at(grind, 'right', rowMidY), at(pb, 'left', rowMidY)), chip: corridorChip(grind, pb) },
    'st-pbtail': { path: line(at(pb, 'right', rowMidY), at(zn, 'left', rowMidY)), chip: corridorChip(pb, zn) },
    'st-zncon': { path: line(at(zn, 'right', rowMidY), at(filter, 'left', rowMidY)), chip: corridorChip(zn, filter) },
    // Pb concentrate over Zn flotation to the thickener: leaves the Pb card's
    // top right of centre, crests over the Zn card, lands on the thickener.
    'st-pbcon': {
      path: `M ${pbconFrom} ${MA_LOCKED_SPINE_Y} C ${pbconFrom} ${MA_LOCKED_ARC_Y + 12}, ${pbconFrom + 24} ${MA_LOCKED_ARC_Y}, ${crestX} ${MA_LOCKED_ARC_Y} C ${pbconTo - 24} ${MA_LOCKED_ARC_Y}, ${pbconTo} ${MA_LOCKED_ARC_Y + 12}, ${pbconTo} ${MA_LOCKED_SPINE_Y}`,
      chip: { x: crestX, y: MA_LOCKED_ARC_Y },
    },
    // Zn tails drop from the Zn card to paste; SA05 sits left of the drop,
    // under the Zn card, clear of the MA lines under the corridor.
    'st-tails': {
      path: `M ${tailsFromX} ${bottom(zn)} C ${tailsFromX} ${bottom(zn) + 24}, ${pasteInX} ${paste.y - 24}, ${pasteInX} ${paste.y}`,
      // Right of the tails edge, growing away from it: with its adherence word
      // inside (`SA05 LATE`) the chip no longer fits the gap to the left,
      // where the Zn water line runs.
      chip: { x: pasteInX + 10, y: mid(bottom(zn), paste.y) + 6, anchor: 'start' },
    },
    'st-paste': { path: line(a('sp-paste', 'right'), a('external-paste', 'left')) },
    'st-pbcake': { path: line(a('sp-filter', 'right', 0.3), a('external-cake', 'left', 0.6)) },
    'st-zncake': { path: line(a('sp-filter', 'right', 0.7), a('external-zncake', 'left', 0.4)) },
    'st-water-mill': { path: line({ x: mid(grind.x, right(grind)), y: water.y }, { x: mid(grind.x, right(grind)), y: bottom(grind) }) },
    'st-water-pb': { path: line({ x: mid(pb.x, right(pb)), y: water.y }, { x: mid(pb.x, right(pb)), y: bottom(pb) }) },
    'st-water-zn': { path: line({ x: znWaterX, y: water.y }, { x: znWaterX, y: bottom(zn) }) },
    'st-tails-of': { path: line({ x: tailsOfX, y: bottom(paste) }, { x: tailsOfX, y: water.y }) },
    'st-conc-of': {
      path: `M ${filterDropX} ${bottom(filter)} C ${filterDropX} ${waterMidY - 40}, ${filterDropX - 20} ${waterMidY}, ${right(water)} ${waterMidY}`,
    },
    'st-makeup': { path: line(a('external-makeup', 'right'), a('sp-water', 'left')) },
  };

  const labelPos: Record<string, readonly MaEdgeLabelPos[]> = {
    'st-zncon': [
      { x: znCorridorX, y: labelY, anchor: 'middle' },
      { x: znCorridorX, y: labelY + MA_LOCKED_LABEL_PITCH, anchor: 'middle' },
    ],
    // Above the arc, beside SA03 on the crest.
    'st-pbcon': [{ x: crestX + 24, y: MA_LOCKED_ARC_Y - 11, anchor: 'start' }],
  };

  return { nodes: placed, boxes, decor, labelPos, top: MA_LOCKED_TOP, naturalBottom };
}

/** The highest point the locked graph draws: `ProcessGraph` places an
 *  invisible bounds node here so `fitView` fits the arc, SA03 and the
 *  Pb-concentrate label, not only the cards. */
export function maLockedBoundsTop(): number {
  return MA_LOCKED_TOP;
}

/** The locked layout's width: its columns and corridors, edge to edge. */
export const MA_LOCKED_WIDTH =
  MA_LOCKED_COLUMNS.reduce((sum, column) => sum + column.w, 0) +
  MA_LOCKED_CORRIDORS.reduce((sum, gap) => sum + gap, 0);

/** fitView (`padding: 0.02`, `maxZoom: 1`) scales the bounds by 1.02. */
const MA_CANVAS_SCALE = 1.02;
const MA_CANVAS_PAD_PX = 4;

/** The zoom `fitView` settles on for the locked graph in a pane this wide:
 *  1 whenever the pane holds {@link MA_LOCKED_WIDTH}, as the flowsheet panel
 *  does from 1400 px. */
export function maLockedZoom(paneWidth?: number): number {
  if (!paneWidth || paneWidth <= 0) return 1;
  return Math.min(1, paneWidth / (MA_LOCKED_WIDTH * MA_CANVAS_SCALE));
}

/** The locked graph's natural pane height at that zoom. */
export function maLockedCanvasHeight(naturalHeight: number, zoom = 1): number {
  return Math.round(naturalHeight * MA_CANVAS_SCALE * zoom) + MA_CANVAS_PAD_PX;
}

/** Graph units a pane this tall offers the stretch in {@link maLockedLayout}. */
export function maLockedFillHeight(paneHeight: number, zoom: number): number {
  return (paneHeight - MA_CANVAS_PAD_PX) / (zoom * MA_CANVAS_SCALE);
}

/** Figure grammar: the closed-circuit loop as a crisp semicircle on the node's top edge. */
export type GraphLoopArc = {
  nodeId: string;
  path: string;
  /** Phase-scaled dot count (grind's train densifies — the 248% leading indicator). */
  dotCount: number;
};

function loopArcs(
  nodeBoxes: Record<string, NodeBox>,
  phase: ScenarioPhase,
  whatIf?: WhatIfDerived | null,
): GraphLoopArc[] {
  return ['sp-grind', 'sp-pb', 'sp-zn'].map((nodeId) => {
    const b = nodeBoxes[nodeId]!;
    const cx = b.x + b.w / 2;
    // Right-to-left over the top: the load exits downstream and returns upstream.
    return {
      nodeId,
      path: `M ${cx + 28} ${b.y} A 28 22 0 0 0 ${cx - 28} ${b.y}`,
      dotCount: loopDotCount(nodeId, phase, whatIf),
    };
  });
}

export type ScenarioGraphOption = {
  phase: ScenarioPhase;
  label: string;
};

export type ProcessGraphView = {
  nodes: GraphNodeView[];
  edges: GraphEdgeView[];
  externalStubs: ExternalStubView[];
  loopLabels: GraphLoopLabel[];
  loopArcs: GraphLoopArc[];
  navOrder: string[];
  scenarioOptions: ScenarioGraphOption[];
  scenarioCaption: string;
  /** Closure strip beneath the graph when the metal-accounting layer is on. */
  maStrip: MaClosureStripView | null;
  /** MA-locked only: the layout's natural height (graph units, before any
   *  stretch) — what the pane is sized from. Null on the plant-model route. */
  maLockedHeight: number | null;
};

export const SCENARIO_GRAPH_OPTIONS: readonly ScenarioGraphOption[] = [
  { phase: 'idle', label: 'Design basis' },
  { phase: 'provisional', label: 'Hardness rising' },
  { phase: 'confirmed', label: 'Coarse grind confirmed' },
];

export const SCENARIO_GRAPH_CAPTION =
  'Hardness step-change scenario — not live historian data.';

export const WHAT_IF_CAPTION =
  'What-if inputs — illustrative only, not a solver.';

// --- What-if inputs (Tim 2026-08-21: "dynamically change various inputs and
// watch various outputs change"). Pure ratio propagation over the §8.1 worked
// example: flows scale with mill feed; hardness coarsens P80 per the §8.5
// argument (design 74 µm → ~88 µm at the walkthrough's hard ore), which lifts
// circulating load and dents flotation recovery. Illustrative, never a solver.

export const WHAT_IF_BOUNDS = {
  feedStph: { min: 55, max: 90, step: 1 },
  hardness: { min: 0.85, max: 1.25, step: 0.05 },
} as const;

export type WhatIfDerived = {
  scale: number;
  p80Um: number;
  circPct: number;
  circUfStph: number;
  znRecPct: number;
  pbRecPct: number;
  grindUtilPct: number;
};

export function whatIfDerived(inputs: WhatIfInputs): WhatIfDerived {
  const scale = inputs.feedStph / WHAT_IF_DEFAULTS.feedStph;
  const h = inputs.hardness;
  // §8.5 calibration: h = 1.25 lands P80 ≈ 88 µm (the walkthrough's confirmed read).
  const p80Um = Math.round(74 * (1 + 0.75 * (h - 1) + 0.2 * Math.max(0, scale - 1)));
  const circPct = Math.round(248 * (1 + 1.6 * (h - 1) + 0.3 * Math.max(0, scale - 1)));
  const circUfStph = Math.round((inputs.feedStph * circPct) / 100);
  const coarsening = Math.max(0, p80Um - 74);
  const pbDesign = READINGS.find((row) => row.id === 'rd-unit-pb01-rec')?.design ?? 84.0;
  const znDesign = READINGS.find((row) => row.id === 'rd-unit-zn01-rec')?.design ?? 87.0;
  const znRecPct = Math.round((znDesign - 0.35 * coarsening) * 10) / 10;
  const pbRecPct = Math.round((pbDesign - 0.3 * coarsening) * 10) / 10;
  const grindUtilPct = Math.min(110, Math.round((inputs.feedStph / 90) * 100 * (1 + 0.35 * (h - 1))));
  return { scale, p80Um, circPct, circUfStph, znRecPct, pbRecPct, grindUtilPct };
}

function fmtInt(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

/** Per-stream scaled flow label; concentrates also track their recovery factor. */
function whatIfFlowLabel(streamId: string, d: WhatIfDerived): string | null {
  switch (streamId) {
    case 'st-crushed':
      return `${fmtInt(75 * d.scale)} stph`;
    case 'st-cyc-of':
      return `${fmtInt(1880 * d.scale)} dst`;
    case 'st-pbtail':
      return `${fmtInt(1728 * d.scale)} dst`;
    case 'st-zncon':
      return `${fmtInt(144 * d.scale * (d.znRecPct / 87))} dst`;
    case 'st-tails':
      return `${fmtInt(1584 * d.scale)} dst`;
    default:
      return null;
  }
}

const CONSTRAINT_PATH_STREAMS = new Set([
  'st-cyc-of',
  'st-pbcon',
  'st-pbtail',
  'st-zncon',
  'st-tails',
  'st-pbcake',
  'st-zncake',
  'st-paste',
]);

function parseFlowMagnitude(stream: Stream): number {
  if (stream.flowReadingId) {
    const reading = READINGS.find((row) => row.id === stream.flowReadingId);
    if (reading?.value !== undefined) return reading.value;
  }
  const flow = stream.nominalFlow;
  const match = flow.match(/[\d,]+(?:\.\d+)?/);
  if (!match) return 0;
  return Number.parseFloat(match[0].replace(/,/g, ''));
}

type FlowDotBucket = 'trickle' | 'normal' | 'bulk';

function flowDotBucket(stream: Stream): FlowDotBucket {
  if (stream.material === 'water' || stream.nominalFlow.trim() === '—') return 'trickle';
  const magnitude = parseFlowMagnitude(stream);
  if (magnitude < 80) return 'trickle';
  if (magnitude < 400) return 'normal';
  return 'bulk';
}

const BUCKET_RADIUS: Record<FlowDotBucket, number> = { trickle: 2, normal: 2.75, bulk: 3.5 };
const BUCKET_DURATION: Record<FlowDotBucket, number> = { trickle: 9, normal: 5.5, bulk: 3.5 };

function flowDots(stream: Stream, flowScale = 1): FlowDotView[] {
  const bucket = flowDotBucket(stream);
  const speedUp = Math.min(1.3, Math.max(0.75, flowScale ** 0.7));
  const duration = (stream.material === 'water' ? 10 : BUCKET_DURATION[bucket]) / speedUp;
  const count = bucket === 'trickle' ? 1 : bucket === 'normal' ? 2 : 3;
  return Array.from({ length: count }, (_, index) => ({
    id: `${stream.id}-dot-${index}`,
    offset: count === 1 ? 0.5 : (index + 0.5) / count,
    radius: BUCKET_RADIUS[bucket] * Math.min(1.25, Math.max(0.8, flowScale ** 0.5)),
    durationSec: duration,
    beginSec: -((duration / count) * index),
  }));
}

/** Grind's loop dot train densifies with the scenario — the 248% leading indicator. */
function loopDotCount(nodeId: string, phase: ScenarioPhase, whatIf?: WhatIfDerived | null): number {
  if (nodeId !== 'sp-grind') return 1;
  if (whatIf) {
    if (whatIf.circPct >= 320) return 3;
    if (whatIf.circPct >= 270) return 2;
    return 1;
  }
  if (phase === 'confirmed') return 3;
  if (phase === 'provisional') return 2;
  return 1;
}

function strokeWidthFor(stream: Stream): number {
  if (stream.material === 'water') return 1.25;
  if (stream.material === 'concentrate') return 1.75;
  return 2.25;
}

function edgeOpacity(stream: Stream, phase: ScenarioPhase): number {
  if (!CONSTRAINT_PATH_STREAMS.has(stream.id)) return 1;
  if (phase === 'idle') return 1;
  if (phase === 'provisional') return 0.55;
  return 0.38;
}

function dotColorFor(stream: Stream): string {
  switch (stream.material) {
    case 'water':
      return 'var(--state-info)';
    case 'concentrate':
      return 'var(--state-online)';
    case 'tailings':
      return 'var(--ink-tertiary)';
    default:
      return 'var(--ink-secondary)';
  }
}

export function adherenceTone(adherence: SampleAdherence): VizTone {
  if (adherence === 'REG') return 'ok';
  // One state word, one tone across the app: a late assay is a warning, only
  // a missed one is a fault (polish.P11 r1, discrepancy row 256).
  if (adherence === 'MISS') return 'bad';
  return 'warn';
}

function sampleChipsOnEdge(
  stream: Stream,
  samplePoints: readonly SamplePoint[],
  chipPos: { x: number; y: number; anchor?: 'middle' | 'start' } | undefined,
  path: string,
): EdgeSampleChip[] {
  if (!chipPos || !path) return [];
  const dot = samplePointOnPath(path, 0.5);
  return stream.samplePointIds.map((id, index) => {
    const point = samplePoints.find((row) => row.id === id);
    const adherence = point?.adherence ?? 'REG';
    const chipAnchor = offsetChipFromDot(dot, { x: chipPos.x + index * 34, y: chipPos.y });
    return {
      id,
      adherence,
      tone: adherenceTone(adherence),
      label: adherence,
      x: chipAnchor.x,
      y: chipAnchor.y,
      anchor: chipPos.anchor ?? 'middle',
      dotX: dot.x,
      dotY: dot.y,
    };
  });
}

function edgeTooltip(stream: Stream, specs: readonly StreamSpec[]): string {
  const specLine =
    specs.length === 0
      ? ''
      : ` · ${specs.map((row) => `${row.parameter} ${row.target}`).join(' · ')}`;
  return `${stream.name} · ${stream.nominalFlow}${specLine}`;
}

/** Sub-line from the phase-applied rank — the % is data, only the label is copy. */
function grindUtilizationSub(phase: ScenarioPhase, utilizationPct: number | null): string {
  const pct = utilizationPct != null ? `${utilizationPct}% of band` : null;
  if (phase === 'idle') return pct ?? 'design basis';
  return pct ?? 'off design';
}

/** Explicit subprocess → graph glyph map (B8). Multi-unit nodes surface the rest in the Units tab. */
const NODE_GLYPH_TYPE: Record<string, UnitOpType> = {
  'sp-crush': 'crusher',
  'sp-grind': 'ball_mill',
  'sp-pb': 'flotation',
  'sp-zn': 'flotation',
  'sp-filter': 'thickener',
  'sp-paste': 'paste',
};

function glyphTypeForNode(nodeId: string): UnitOpType | null {
  return NODE_GLYPH_TYPE[nodeId] ?? null;
}

/**
 * Circuit colour grammar (plant-flowsheet.png reference):
 * intermediate streams take the colour of the circuit they feed into;
 * product / tailings streams keep the source node's circuit;
 * crushing / grinding feed stays ink.
 */
function circuitForStream(stream: Stream): CircuitKind {
  if (stream.material === 'water') return 'none';

  switch (stream.id) {
    case 'st-rom':
    case 'st-crushed':
    case 'st-cyc-of':
      return 'none';
    case 'st-pbtail':
      return 'zn';
    case 'st-pbcon':
    case 'st-pbcake':
      return 'pb';
    case 'st-zncon':
    case 'st-zncake':
    case 'st-tails':
      return 'zn';
    default:
      return 'none';
  }
}

function nodeTopTone(
  node: PlantStripNode,
  phase: ScenarioPhase,
  state?: SessionState,
  readings = READINGS,
): string {
  if (state) {
    const tone = scopeHealth({ kind: 'subprocess', id: node.id }, readings, state);
    if (tone === 'bad') return 'border-t-[3px] border-t-[var(--state-blocked)]';
    if (tone === 'warn') return 'border-t-[3px] border-t-[var(--state-warning)]';
  }
  if (!node.isBottleneck) return '';
  if (phase === 'idle') return 'border-t-[3px] border-t-[var(--state-warning)]';
  return 'border-t-[3px] border-t-[var(--state-blocked)]';
}

/** The bottleneck's state tone on the scripted phase: warning at the design
 *  basis, blocked once hardness is flagged. Null off the bottleneck. */
export function bottleneckTone(node: PlantStripNode, phase: ScenarioPhase): 'warn' | 'bad' | null {
  if (!node.isBottleneck) return null;
  return phase === 'idle' ? 'warn' : 'bad';
}

function nodeBarClass(node: PlantStripNode, phase: ScenarioPhase): string {
  const tone = bottleneckTone(node, phase);
  if (!tone) return 'bg-zinc-400 dark:bg-zinc-500';
  return tone === 'warn' ? 'bg-[var(--state-warning)]' : 'bg-[var(--state-blocked)]';
}

export function bottleneckLabel(node: PlantStripNode, phase: ScenarioPhase): string | null {
  if (!node.isBottleneck) return node.utility ? 'Utility loop' : null;
  if (phase === 'idle') return 'Bottleneck';
  if (phase === 'provisional') return 'Stressed';
  return 'Live constraint';
}

/** What-if tone thresholds on grind utilization. */
function whatIfGrindState(utilPct: number): { tone: string; bar: string; label: string } {
  if (utilPct >= 97) {
    return {
      tone: 'border-t-[3px] border-t-[var(--state-blocked)]',
      bar: 'bg-[var(--state-blocked)]',
      label: 'Overloaded',
    };
  }
  if (utilPct >= 88) {
    return {
      tone: 'border-t-[3px] border-t-[var(--state-warning)]',
      bar: 'bg-[var(--state-warning)]',
      label: 'Stressed',
    };
  }
  return {
    tone: 'border-t-[3px] border-t-[var(--state-warning)]',
    bar: 'bg-[var(--state-warning)]',
    label: 'Bottleneck',
  };
}

/** Pb / Zn cards name recovery basis — live actual plus design where the seed carries it. */
function recoverySubLine(
  nodeId: string,
  readings: readonly typeof READINGS[number][],
): string | null {
  if (nodeId !== 'sp-pb' && nodeId !== 'sp-zn') return null;
  const recId = nodeId === 'sp-pb' ? 'rd-unit-pb01-rec' : 'rd-unit-zn01-rec';
  const rec = readings.find((row) => row.id === recId);
  if (!rec || rec.value === undefined) return null;
  const parts: string[] = [`live ${rec.value.toFixed(1)}%`];
  if (nodeId === 'sp-pb') {
    const design = rec.design;
    if (design !== undefined) parts.push(`design ${design.toFixed(1)}%`);
  }
  return parts.join(' · ');
}

export function processGraph(
  ranks: readonly ResolvedUtilizationRank[],
  phase: ScenarioPhase,
  samplePoints: readonly SamplePoint[],
  state: SessionState | null = null,
  whatIfInputs: WhatIfInputs | null = null,
  maActive = false,
  lens: RollupLens = 'met',
  period: AccountingPeriod = 'day',
  streams: readonly Stream[] = STREAMS,
  loops: readonly RecirculatingLoop[] = RECIRCULATING_LOOPS,
  subprocesses: readonly Subprocess[] = SUBPROCESSES,
  units: readonly UnitOperation[] = UNIT_OPERATIONS,
  specs: readonly StreamSpec[] = STREAM_SPECS,
  view?: PeriodView,
  /** MA-locked only: graph units the host pane offers (see `maLockedLayout`). */
  lockedFillHeight?: number,
): ProcessGraphView {
  const layout = buildGraphLayout(streams, samplePoints, !maActive, lens !== 'gm');
  const stripNodes = plantStrip(ranks, streams, loops, subprocesses, units);
  const resolvedView = view ?? periodWindows()[period];
  const whatIf =
    view || !whatIfInputs || isWhatIfDefault(whatIfInputs) ? null : whatIfDerived(whatIfInputs);
  const ma = maActive ? metalAccountingLayer(resolvedView) : null;
  const readings = state ? readingsAt(state) : READINGS;

  /** MA-locked = the `/metal-accounting` route (a `PeriodView`, never a
   *  what-if). `periodLabelsOnly` and the card suppression both hang off it. */
  const maLocked = maActive && view !== undefined;

  // GM lens (#169, A §M4): nodes keep status only, tag-level labels collapse —
  // the roll-up strip above the canvas carries the compressed figures.
  const gm = lens === 'gm';

  const stripNodeViews: GraphNodeView[] = stripNodes.map((node) => {
    const box = layout.nodeBoxes[node.id] ?? { x: 0, y: 0, w: SPINE_BOX.w, h: SPINE_BOX.h };
    const isWater = node.id === 'sp-water';
    const waterSub = subprocesses.find((row) => row.id === 'sp-water')?.inputs ?? null;
    const interior = interiorEdgeChipsForNode(node.id, streams, samplePoints);
    const recoverySub = recoverySubLine(node.id, readings);
    const base = {
      ...node,
      ...box,
      topToneClass: nodeTopTone(node, phase, state ?? undefined, readings),
      barClass: nodeBarClass(node, phase),
      bottleneckLabel: bottleneckLabel(node, phase),
      wide: isWater,
      interiorEdges: interior.visible,
      interiorOverflow: interior.overflow,
      unitOpType: isWater ? null : glyphTypeForNode(node.id),
      maNote: ma?.nodeNotes[node.id] ?? null,
      maGap: maLocked ? ma?.nodeGaps[node.id] ?? null : null,
      // Set by `maLockedLayout` on the MA-locked path only.
      maCompact: false,
      headline: isWater ? null : node.headline,
      sub:
        node.id === 'sp-grind'
          ? grindUtilizationSub(phase, node.utilizationPct ?? null)
          : isWater
            ? waterSub
            : recoverySub ?? node.sub,
      // Loop annotation moves below the node (figure grammar) — not an in-cell line.
      recycleBadge: null,
    };
    // MA-locked: the plant-model headline / sub are not period reads, and the
    // utilization bar / bottleneck label are design-basis constraint reads —
    // none of them belong on a period-scoped route. Suppressed *here*, inside
    // the transform, rather than in the tsx overlay: `maLockedLayout`
    // below sizes each card from what it renders, so a card nulled after the
    // compaction measured it collapses to nothing and the full-height empty box
    // comes back (r11 MUST-FIX 1). `maNote` is the one period read that gives a
    // card a body, and only the day view writes one.
    if (maLocked) {
      return suppressConstraintReadsOnNode({
        ...base,
        headline: null,
        maNote: resolvedView.period === 'shift' ? null : base.maNote,
      });
    }
    if (!whatIf) return base;
    const feed = `${fmtInt(75 * whatIf.scale)} stph`;
    switch (node.id) {
      case 'sp-crush':
        return { ...base, headline: feed };
      case 'sp-grind': {
        const grindState = whatIfGrindState(whatIf.grindUtilPct);
        return {
          ...base,
          headline: feed,
          sub: `${whatIf.grindUtilPct}% · P80 ${whatIf.p80Um} µm`,
          utilizationPct: whatIf.grindUtilPct,
          topToneClass: grindState.tone,
          barClass: grindState.bar,
          bottleneckLabel: grindState.label,
        };
      }
      case 'sp-pb': {
        const pbDesign = readings.find((row) => row.id === 'rd-unit-pb01-rec')?.design;
        return {
          ...base,
          sub:
            pbDesign !== undefined
              ? `live ${whatIf.pbRecPct.toFixed(1)} % · design ${pbDesign.toFixed(1)} %`
              : `live ${whatIf.pbRecPct.toFixed(1)} %`,
        };
      }
      case 'sp-zn':
        return { ...base, sub: `live ${whatIf.znRecPct.toFixed(1)} %` };
      case 'sp-paste':
        return { ...base, headline: `${fmtInt(318 * whatIf.scale)} dst/d backfill` };
      default:
        return base;
    }
  });

  // GM lens (#169, A §M4): nodes keep status only, tag-level labels collapse —
  // the roll-up strip above the canvas carries the compressed figures.
  //
  // Applied *before* the compaction below, not at the return: a card's box has
  // to be sized from what it renders, and a body field cleared after the
  // measurement gives back the full-height empty box (r11 MUST-FIX 1). On the
  // plant-model route this is height-neutral — those boxes come from
  // `buildGraphLayout`'s own chip counts, not from the node views.
  const rendered = gm
    ? stripNodeViews.map((node) => ({
        ...node,
        headline: null,
        sub: null,
        interiorEdges: [],
        interiorOverflow: 0,
      }))
    : stripNodeViews;

  // MA-locked: the compact layout sizes every card from what it renders, then
  // places each edge, chip and label against those boxes (r10 MUST-FIX 3).
  const locked = maLocked ? maLockedLayout(rendered, lockedFillHeight) : null;
  const nodes = locked ? locked.nodes : rendered;
  const nodeBoxes = locked ? locked.boxes : layout.nodeBoxes;
  const maxSpineBottom = layout.maxSpineBottom;
  const decor = locked ? locked.decor : edgeDecor(nodeBoxes, streams);
  const maLabelPos = locked ? locked.labelPos : MA_EDGE_LABEL_POS;

  // A period-scoped route renders period reads or nothing: plant-model nominals
  // (`75 stph`, `1,880 dst`, `186 stph · 248 %`) are design constants that read
  // as measurements when they sit unchanged on Shift / 24 h / WTD / MTD
  // (r10 MUST-FIX 4, carrying `ProcessGraph.tsx`'s headline/sub rule to labels).
  const periodLabelsOnly = maLocked;

  const edges: GraphEdgeView[] = streams
    .filter((stream) => !SELF_LOOP_STREAMS.has(stream.id))
    .map((stream) => {
      const d = decor[stream.id];
      const streamSpecs = specs.filter((row) => row.streamId === stream.id);
      const whatIfText = whatIf ? whatIfFlowLabel(stream.id, whatIf) : null;
      const label = d?.label ? { ...d.label, anchor: d.label.anchor ?? 'middle' } : null;
      const maLines = ma?.edgeLabels[stream.id] ?? [];
      const maPos = maLabelPos[stream.id] ?? [];
      const maLabels: MaEdgeLabel[] = maLines
        .slice(0, maPos.length)
        .map((line, index) => ({ ...line, ...maPos[index]! }));
      return {
        stream,
        path: d?.path ?? '',
        strokeWidth: strokeWidthFor(stream),
        dashed: stream.material === 'water',
        opacity: whatIf ? 1 : edgeOpacity(stream, phase),
        tooltipTitle: edgeTooltip(stream, streamSpecs),
        // The MA layer replaces the mass label where it writes its own lines.
        valueLabel:
          maLabels.length > 0
            ? null
            : label && whatIfText
              ? { ...label, text: whatIfText }
              : periodLabelsOnly
                ? null
                : label,
        sampleChips: sampleChipsOnEdge(stream, samplePoints, d?.chip, d?.path ?? ''),
        dots: flowDots(stream, whatIf && stream.material !== 'water' ? whatIf.scale : 1),
        dotColor: dotColorFor(stream),
        circuit: circuitForStream(stream),
        maLabels,
      };
    })
    .filter((edge) => edge.path !== '');

  const loopRows = gm ? [] : loopLabels(loops, streams, maxSpineBottom, whatIf, periodLabelsOnly);
  return {
    nodes,
    edges: gm
      ? edges.map((edge) => ({ ...edge, valueLabel: null, sampleChips: [] }))
      : edges,
    externalStubs: externalStubs(nodeBoxes, whatIf, resolvedView, periodLabelsOnly).map((stub) =>
      locked ? { ...stub, wrap: true } : stub,
    ),
    // A loop annotation with no period figure is omitted, not dashed out.
    loopLabels: periodLabelsOnly ? loopRows.filter((row) => row.value !== '') : loopRows,
    // The MA-locked route draws no recirculation arcs. Its legend no longer
    // decodes them (r11 SHOULD-FIX 4 kept the graphic and dropped the decode),
    // and an undecoded glyph is worse than no glyph — DESIGN.md § *Decided*
    // keeps legend decodes on glass precisely because they tell the reader how
    // to read a mark. The closed-circuit topology is the plant-model route's
    // story; the period route's is the mass, and that is carried by the cards,
    // the edge labels and the stubs (r11 SHOULD-FIX 3).
    loopArcs: maLocked ? [] : loopArcs(nodeBoxes, phase, whatIf),
    navOrder: stripNodeOrder(streams, subprocesses),
    scenarioOptions: [...SCENARIO_GRAPH_OPTIONS],
    scenarioCaption: whatIf ? WHAT_IF_CAPTION : SCENARIO_GRAPH_CAPTION,
    maStrip: ma?.strip ?? null,
    maLockedHeight: locked ? locked.naturalBottom - locked.top : null,
  };
}

/** Metal-accounting graph — PeriodView only, no what-if parameter (R14). */
export function metalAccountingProcessGraph(
  ranks: readonly ResolvedUtilizationRank[],
  phase: ScenarioPhase,
  samplePoints: readonly SamplePoint[],
  state: SessionState | null,
  lens: RollupLens,
  view: PeriodView,
  options: { fillHeight?: number } = {},
  streams: readonly Stream[] = STREAMS,
  loops: readonly RecirculatingLoop[] = RECIRCULATING_LOOPS,
  subprocesses: readonly Subprocess[] = SUBPROCESSES,
  units: readonly UnitOperation[] = UNIT_OPERATIONS,
  specs: readonly StreamSpec[] = STREAM_SPECS,
): ProcessGraphView {
  return processGraph(
    ranks,
    phase,
    samplePoints,
    state,
    null,
    true,
    lens,
    view.period,
    streams,
    loops,
    subprocesses,
    units,
    specs,
    view,
    options.fillHeight,
  );
}
